<template>
  <div>
    <layout-header></layout-header>
    <layout-sidebar></layout-sidebar>

    <div class="page-wrapper">
      <div class="content">
        <div class="page-header">
          <div class="page-title">
            <h4>Catégories de produits</h4>
            <h6>Paramétrez les familles de produits de votre société</h6>
          </div>
          <div class="page-btn">
            <button
              class="btn btn-added"
              data-bs-toggle="modal"
              data-bs-target="#categoryModal"
              @click="openCreate"
            >
              <vue-feather type="plus-circle" class="me-2"></vue-feather>
              Nouvelle catégorie
            </button>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <div class="row g-3 align-items-center mb-3">
              <div class="col-md-6">
                <div class="input-group">
                  <span class="input-group-text bg-white">
                    <vue-feather type="search" size="16"></vue-feather>
                  </span>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Rechercher une catégorie"
                    v-model="search"
                  />
                </div>
              </div>
              <div class="col-md-6 text-end">
                <button class="btn btn-outline-secondary" @click.prevent="loadCategories">
                  <vue-feather type="rotate-ccw" size="16" class="me-1"></vue-feather>
                  Rafraîchir
                </button>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th class="text-center">Produits liés</th>
                    <th>Date de création</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cat in filteredCategories" :key="cat.id">
                    <td>{{ cat.nom }}</td>
                    <td class="text-center">{{ cat.produits_count ?? 0 }}</td>
                    <td>{{ formatDate(cat.created_at) }}</td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-outline-primary me-2" @click="openEdit(cat)">
                        <vue-feather type="edit-2" size="14" class="me-1"></vue-feather>
                        Modifier
                      </button>
                      <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(cat)">
                        <vue-feather type="trash-2" size="14" class="me-1"></vue-feather>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!filteredCategories.length">
                    <td colspan="4" class="text-center text-muted py-4">
                      Aucune catégorie pour le moment.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="categoryModal"
      tabindex="-1"
      aria-labelledby="categoryModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="categoryModalLabel">
              {{ modal.id ? "Modifier la catégorie" : "Nouvelle catégorie" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="input-blocks">
              <label class="form-label">Nom</label>
              <input
                type="text"
                class="form-control"
                placeholder="Ex : Accessoires, Consommables..."
                v-model="modal.nom"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Annuler</button>
            <button type="button" class="btn btn-primary" :disabled="saving" @click="saveCategory">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ modal.id ? "Mettre à jour" : "Créer" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchProductCategories,
  createProductCategory,
  updateProductCategory,
  deleteProductCategory,
} from "@/services/productCategoryService";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { toast } from "vue3-toastify";

export default {
  name: "ProductCategories",
  data() {
    return {
      categories: [],
      search: "",
      loading: false,
      saving: false,
      modal: {
        id: null,
        nom: "",
      },
    };
  },
  computed: {
    filteredCategories() {
      const term = this.search.trim().toLowerCase();
      if (!term) return this.categories;
      return this.categories.filter((cat) =>
        (cat.nom || "").toLowerCase().includes(term)
      );
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "—";
      try {
        return new Intl.DateTimeFormat("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(date));
      } catch (_) {
        return date;
      }
    },
    resetModal() {
      this.modal = { id: null, nom: "" };
    },
    openCreate() {
      this.resetModal();
    },
    openEdit(cat) {
      this.modal = { id: cat.id, nom: cat.nom };
      const el = document.getElementById("categoryModal");
      Modal.getOrCreateInstance(el).show();
    },
    async loadCategories() {
      this.loading = true;
      try {
        const data = await fetchProductCategories();
        // add produits_count when relation is loaded
        this.categories = (data || []).map((cat) => ({
          ...cat,
          produits_count: cat.produits_count ?? cat.produits?.length ?? 0,
        }));
      } catch (error) {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Impossible de charger les catégories."
        );
      } finally {
        this.loading = false;
      }
    },
    async saveCategory() {
      if (!this.modal.nom.trim()) {
        toast.error("Le nom de la catégorie est requis.");
        return;
      }

      this.saving = true;
      try {
        if (this.modal.id) {
          await updateProductCategory(this.modal.id, { nom: this.modal.nom });
        } else {
          await createProductCategory({ nom: this.modal.nom });
        }
        await this.loadCategories();
        this.hideModal();
        toast.success("Catégorie enregistrée avec succès.");
      } catch (error) {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Impossible d'enregistrer la catégorie."
        );
      } finally {
        this.saving = false;
      }
    },
    hideModal() {
      const el = document.getElementById("categoryModal");
      const modal = Modal.getInstance(el);
      if (modal) {
        modal.hide();
      }
    },
    async confirmDelete(cat) {
      const result = await Swal.fire({
        title: "Supprimer cette catégorie ?",
        text: "Les produits liés conserveront l'intitulé actuel.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#d33",
      });

      if (!result.value) {
        return;
      }

      try {
        await deleteProductCategory(cat.id);
        await this.loadCategories();
        toast.success("Catégorie supprimée.");
      } catch (error) {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Suppression impossible."
        );
      }
    },
  },
  mounted() {
    this.loadCategories();
  },
};
</script>

<style scoped>
.page-wrapper {
  background: #f5f6fa;
}

.table td,
.table th {
  vertical-align: middle;
}

.input-blocks {
  margin-bottom: 12px;
}
</style>
